import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router";
import {
  PlusCircleIcon,
  ArrowLeftIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { createIncidentReport, resolveWHSToken } from "services/whs/whsService";
import { useAuthContext } from "app/contexts/auth/context";
import { toast } from "sonner";
import sunnyHomesCrmLogo from "assets/images/SH-logo.png";
import { JWT_HOST_API } from "configs/auth.config";
import { isSunnyHomes } from "./whs-qr/InputFormatters";

// Shared WHS Modular Primitives
import { getTodayInputDate, getCurrentFormattedTime } from "./whs/shared/whsHelpers";
import WhsPageLayout from "./whs/shared/WhsPageLayout";
import WhsJobBanner from "./whs/shared/WhsJobBanner";
import WhsSuccessCountdown from "./whs/shared/WhsSuccessCountdown";
import useIncidentDropdowns from "./whs/incident/useIncidentDropdowns";
import IncidentFormFields from "./whs/incident/IncidentFormFields";

export default function WhsReportIncidentPage() {
  const { token: paramToken } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: loggedInUser } = useAuthContext();

  const token =
    paramToken || location.state?.token || localStorage.getItem("whs_pending_token") || "";

  // Resolved site & company state
  const [jobData, setJobData] = useState(() => {
    if (location.state?.jobData) return location.state.jobData;
    try {
      const cached = localStorage.getItem("whs_job_data");
      if (cached) return JSON.parse(cached);
    } catch (e) {
      console.error("Failed to parse cached whs_job_data:", e);
    }
    return null;
  });

  const [companyId, setCompanyId] = useState(() => {
    const initialJob =
      location.state?.jobData ||
      (() => {
        try {
          const cached = localStorage.getItem("whs_job_data");
          return cached ? JSON.parse(cached) : null;
        } catch {
          return null;
        }
      })();
    return (
      initialJob?.companyId ||
      initialJob?.CompanyId ||
      loggedInUser?.companyId ||
      loggedInUser?.CompanyId ||
      ""
    );
  });
  const [companyLogo, setCompanyLogo] = useState(sunnyHomesCrmLogo);

  // Form State
  const [type, setType] = useState("");
  const [date, setDate] = useState(getTodayInputDate());
  const [time, setTime] = useState(getCurrentFormattedTime(true));
  const [reportedBy, setReportedBy] = useState("");
  const [witnesses, setWitnesses] = useState([]);
  const [locationName, setLocationName] = useState("");
  const [severity, setSeverity] = useState("");
  const [status, setStatus] = useState("");
  const [description, setDescription] = useState("");
  const [correctiveAction, setCorrectiveAction] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // Submission & Success state
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [submittedSummary, setSubmittedSummary] = useState(null);

  const dynamicFieldsRef = useRef(null);

  const isInternalUser = useMemo(() => {
    if (
      location.state?.isInternalUser === true ||
      location.state?.userForm?.isInternalUser === true ||
      localStorage.getItem("whs_is_internal_user") === "true"
    ) {
      return true;
    }
    if (localStorage.getItem("whs_username") && localStorage.getItem("authToken")) {
      return true;
    }
    if (
      loggedInUser &&
      (loggedInUser.isInternalUser ||
        loggedInUser.IsInternalUser ||
        loggedInUser.isInternalRole ||
        (!loggedInUser.externalCompanyId && isSunnyHomes(loggedInUser.companyName)))
    ) {
      return true;
    }
    if (
      localStorage.getItem("whs_is_internal_user") === "false" ||
      location.state?.userForm?.isInternalUser === false
    ) {
      return false;
    }
    return true;
  }, [location.state, loggedInUser]);

  // Set Company Logo
  useEffect(() => {
    try {
      const hostname = window.location.hostname;
      let folderName = hostname;
      if (hostname.includes("localhost") || hostname.includes("127.0.0.1")) {
        folderName = "localhost";
      }
      const apiHost = JWT_HOST_API?.endsWith("/") ? JWT_HOST_API : `${JWT_HOST_API}/`;
      setCompanyLogo(`${apiHost}CompanyLogos/${folderName}/main.png`);
    } catch (err) {
      console.error("Failed to set company logo from URL:", err);
    }
  }, []);

  // Resolve QR token & context
  useEffect(() => {
    let isMounted = true;
    const resolveContext = async () => {
      let initialJobData = jobData;
      if (!initialJobData) {
        try {
          const cached = localStorage.getItem("whs_job_data");
          if (cached) {
            initialJobData = JSON.parse(cached);
            if (isMounted) setJobData(initialJobData);
          }
        } catch (e) {
          console.error("Failed to parse cached whs_job_data:", e);
        }
      }

      if (token && !initialJobData) {
        try {
          const result = await resolveWHSToken(token);
          const resolved = result?.data || result;
          if (isMounted && resolved) {
            setJobData(resolved);
            initialJobData = resolved;
            localStorage.setItem("whs_job_data", JSON.stringify(resolved));
          }
        } catch (err) {
          console.error("Failed to resolve WHS token:", err);
        }
      }

      const activeCompId =
        initialJobData?.companyId ||
        initialJobData?.CompanyId ||
        loggedInUser?.companyId ||
        loggedInUser?.CompanyId ||
        "";
      if (isMounted) setCompanyId(activeCompId);

      const savedPhone = localStorage.getItem("whs_phone_number") || "";
      const savedUserId = localStorage.getItem("whs_user_id") || "";
      const authName =
        loggedInUser?.FullName ||
        loggedInUser?.fullName ||
        loggedInUser?.name ||
        location.state?.userForm?.fullName ||
        "";
      const initialReporter = authName || savedPhone || savedUserId || "";
      if (initialReporter && isMounted) {
        setReportedBy(initialReporter);
      }
    };

    resolveContext();
    return () => {
      isMounted = false;
    };
  }, [token]);

  // Modular Incident Dropdown hook
  const {
    typeOptions,
    severityOptions,
    statuses,
    internalUserOptions,
    allUserOptions,
    loadingDropdowns,
    handleCreateIncidentType,
    handleEditIncidentType,
    handleDeleteIncidentType,
    handleCreateSeverity,
    handleEditSeverity,
    handleDeleteSeverity,
  } = useIncidentDropdowns({
    companyId,
    locationState: location.state,
    loggedInUser,
    reportedBy,
    type,
    setType,
    severity,
    setSeverity,
    status,
    setStatus,
  });

  const typeOption =
    typeOptions.find(
      (opt) => String(opt.value)?.toLowerCase() === String(type)?.toLowerCase()
    ) || (type ? { value: type, label: type } : null);

  const severityOption =
    severityOptions.find(
      (opt) => String(opt.value)?.toLowerCase() === String(severity)?.toLowerCase()
    ) || (severity ? { value: severity, label: severity } : null);

  // Return to attendance navigation
  const handleReturnNow = () => {
    const returnToken = token || paramToken || "";
    const activeUserId =
      location.state?.userId ||
      location.state?.userForm?.userId ||
      localStorage.getItem("whs_user_id") ||
      null;
    const activeCompId =
      companyId ||
      jobData?.companyId ||
      jobData?.CompanyId ||
      localStorage.getItem("whs_company_id") ||
      null;

    try {
      localStorage.setItem("whs_is_internal_user", "true");
    } catch (storageErr) {
      console.warn("Storage error:", storageErr);
    }
    navigate(returnToken ? `/whs/${returnToken}` : "/whs", {
      state: {
        jobData,
        companyId: activeCompId,
        token: returnToken,
        userId: activeUserId,
        isInternalUser: true,
        userForm: location.state?.userForm
          ? { ...location.state.userForm, isInternalUser: true }
          : undefined,
        activeCheckInDate: location.state?.activeCheckInDate,
        isCheckedIn: location.state?.isCheckedIn ?? true,
        workerStatus: location.state?.workerStatus || null,
        fromMenu: true,
      },
    });
  };

  // Auto-redirect timer on successful submission
  useEffect(() => {
    if (!isSubmitted) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleReturnNow();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isSubmitted]);

  // Validation
  const hasAnyField =
    Boolean(type) ||
    Boolean(severity) ||
    Boolean(locationName?.trim()) ||
    Boolean(description?.trim()) ||
    Boolean(correctiveAction?.trim()) ||
    witnesses.length > 0 ||
    uploadedFiles.length > 0;

  // Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationAttempted(true);

    if (!hasAnyField) {
      toast.error("Please fill in at least one field to submit the incident report.");
      return;
    }

    try {
      setIsSubmitting(true);

      let incidentDateTime = null;
      if (date) {
        let tPart = "00:00:00";
        if (time) {
          const cleanTime = time.trim();
          const match = cleanTime.match(/(\d+):(\d+)\s*(AM|PM)?/i);
          if (match) {
            let h = parseInt(match[1], 10);
            const m = parseInt(match[2], 10);
            const ampm = match[3] ? match[3].toUpperCase() : null;
            if (ampm === "PM" && h < 12) h += 12;
            if (ampm === "AM" && h === 12) h = 0;
            tPart = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`;
          } else if (time.includes(":")) {
            const parts = time.split(":");
            tPart = `${String(parts[0]).padStart(2, "0")}:${String(parts[1]).padStart(2, "0")}:00`;
          }
        }
        incidentDateTime = `${date}T${tPart}`;
      }

      const mappedWitnesses = witnesses.map((selectedValue) => {
        const selectedKey = String(selectedValue);
        const selectedUser =
          allUserOptions.find(
            (u) =>
              String(u.value) === selectedKey ||
              String(u.userId) === selectedKey ||
              String(u.label) === selectedKey
          ) ||
          internalUserOptions.find(
            (u) =>
              String(u.value) === selectedKey ||
              String(u.userId) === selectedKey ||
              String(u.label) === selectedKey
          );
        return {
          Id: null,
          WitnessName: selectedUser?.label ?? selectedKey,
          UserId: selectedUser?.userId ?? null,
        };
      });

      const activeJobId = jobData?.jobId || jobData?.JobId || location.state?.jobData?.jobId || null;
      const activeCompId =
        companyId ||
        jobData?.companyId ||
        jobData?.CompanyId ||
        loggedInUser?.companyId ||
        null;

      const activeUserId =
        location.state?.userId ||
        location.state?.userForm?.userId ||
        localStorage.getItem("whs_user_id") ||
        internalUserOptions.find(
          (u) =>
            u.value === reportedBy ||
            u.label === reportedBy ||
            u.userId === reportedBy
        )?.userId ||
        allUserOptions.find(
          (u) =>
            u.value === reportedBy ||
            u.label === reportedBy ||
            u.userId === reportedBy
        )?.userId ||
        loggedInUser?.id ||
        loggedInUser?.userId ||
        null;

      if (!activeJobId) {
        toast.error("Job site context is missing. Please return to the QR check-in page.");
        setIsSubmitting(false);
        return;
      }

      if (!activeUserId) {
        toast.error("Reporting user could not be identified. Please ensure you are checked in.");
        setIsSubmitting(false);
        return;
      }

      const record = {
        JobId: activeJobId,
        CompanyId: activeCompId,
        UserId: activeUserId,
        IncidentTypeId: type || null,
        IncidentDateTime: incidentDateTime,
        Location: locationName?.trim() || null,
        ReportedBy: reportedBy?.trim() || null,
        SeverityLevelId: severity || null,
        StatusId: status || null,
        Description: description?.trim() || null,
        CorrectiveAction: correctiveAction?.trim() || null,
        Witnesses: mappedWitnesses,
        Attachments: uploadedFiles,
      };

      const res = await createIncidentReport(record, activeCompId);

      if (res?.statusCode === 200 || res?.status === "Success" || res?.data) {
        const targetIncidentId =
          res?.IncidentId ||
          res?.incidentId ||
          res?.data?.IncidentId ||
          res?.data?.incidentId ||
          res?.data ||
          res?.IncidentReportId;

        if (targetIncidentId && dynamicFieldsRef.current) {
          try {
            await dynamicFieldsRef.current.save(targetIncidentId);
          } catch (dfErr) {
            console.error("Failed to save dynamic fields for incident:", dfErr);
          }
        }

        const selectedType = typeOption?.label || "General Incident";
        setSubmittedSummary({
          jobTitle: jobData?.jobTitle || (jobData?.displayJobId ? `Job #${jobData.displayJobId}` : "Site Project"),
          type: selectedType,
          date: date,
          time: time,
          reportedBy: reportedBy || "Site Worker",
        });

        setIsSubmitted(true);
        setCountdown(5);
        try {
          localStorage.setItem("whs_is_internal_user", "true");
        } catch (storageErr) {
          console.warn("Storage error:", storageErr);
        }
        toast.success("Incident report submitted successfully!");
      } else {
        toast.error(res?.message || "Failed to submit incident report.");
      }
    } catch (err) {
      console.error("Error submitting incident report:", err);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "An error occurred while submitting the incident report."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isCheckedIn = Boolean(
    location.state?.isCheckedIn ||
    location.state?.activeCheckInDate ||
    localStorage.getItem("whs_is_checked_in") === "true"
  );
  const showSideMenu = isInternalUser && isCheckedIn;

  return (
    <WhsPageLayout
      title="Report WHS Incident"
      pageHeaderTitle="Report WHS Incident"
      subtitle="Site Safety & Incident Logging System"
      currentPage="report-incident"
      token={token}
      jobData={jobData}
      userForm={location.state?.userForm}
      showSideMenu={showSideMenu}
      companyLogo={companyLogo}
      maxWidth="sm:max-w-2xl"
    >
      {isSubmitted ? (
        <WhsSuccessCountdown
          title="Incident Report Submitted Successfully"
          description="Thank you for notifying site management. Your report has been logged and the safety team has been alerted."
          countdown={countdown}
          onReturn={handleReturnNow}
          summaryCard={
            submittedSummary && (
              <div className="bg-slate-50/90 border border-slate-200/80 p-3.5 rounded-2xl text-left space-y-2.5 shadow-xs font-sans">
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                    Job Site
                  </p>
                  <p className="text-xs font-bold text-slate-900">
                    {submittedSummary.jobTitle}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-1 border-t border-slate-200/60">
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                      Incident Type
                    </p>
                    <p className="text-xs font-bold text-slate-700">
                      {submittedSummary.type}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                      Reported At
                    </p>
                    <p className="text-xs font-bold text-slate-700">
                      {submittedSummary.date} {submittedSummary.time}
                    </p>
                  </div>
                </div>
              </div>
            )
          }
        />
      ) : (
        <div className="space-y-4 font-sans">
          {/* Header Navigation & Add Fields Action */}
          <div className="flex items-center justify-between pb-3 border-b border-slate-200/60">
            <button
              type="button"
              onClick={handleReturnNow}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-900 transition-colors cursor-pointer"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              <span>Back to Attendance</span>
            </button>

            <button
              type="button"
              onClick={() => dynamicFieldsRef.current?.openAddFieldModal()}
              className="text-primary-600 hover:text-primary-700 hover:bg-primary-50 border border-primary-200 inline-flex cursor-pointer items-center gap-1 rounded-xl px-2.5 py-1 text-xs font-bold transition-all shadow-2xs"
            >
              <PlusCircleIcon className="h-3.5 w-3.5" />
              <span>Add Fields</span>
            </button>
          </div>

          {/* Job Site Context Banner */}
          <WhsJobBanner jobData={jobData} />

          {/* Validation Banner */}
          {validationAttempted && !hasAnyField && (
            <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-xs text-rose-700 flex items-center gap-2">
              <ExclamationTriangleIcon className="size-4 shrink-0 text-rose-500" />
              <span>Please fill in at least one field to submit the incident report.</span>
            </div>
          )}

          {/* Form Fields Component */}
          <IncidentFormFields
            type={type}
            setType={setType}
            typeOptions={typeOptions}
            typeOption={typeOption}
            handleCreateIncidentType={handleCreateIncidentType}
            handleEditIncidentType={handleEditIncidentType}
            handleDeleteIncidentType={handleDeleteIncidentType}
            severity={severity}
            setSeverity={setSeverity}
            severityOptions={severityOptions}
            severityOption={severityOption}
            handleCreateSeverity={handleCreateSeverity}
            handleEditSeverity={handleEditSeverity}
            handleDeleteSeverity={handleDeleteSeverity}
            date={date}
            setDate={setDate}
            time={time}
            setTime={setTime}
            reportedBy={reportedBy}
            setReportedBy={setReportedBy}
            internalUserOptions={internalUserOptions}
            witnesses={witnesses}
            setWitnesses={setWitnesses}
            allUserOptions={allUserOptions}
            locationName={locationName}
            setLocationName={setLocationName}
            status={status}
            setStatus={setStatus}
            statuses={statuses}
            description={description}
            setDescription={setDescription}
            correctiveAction={correctiveAction}
            setCorrectiveAction={setCorrectiveAction}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            dynamicFieldsRef={dynamicFieldsRef}
            companyId={companyId}
            loggedInUser={loggedInUser}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
            onCancel={handleReturnNow}
            loadingDropdowns={loadingDropdowns}
          />
        </div>
      )}
    </WhsPageLayout>
  );
}
